import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { useAuth, isWriter } from "@/context/AuthContext";
import { PageHeader, SourceLink, EmptyState } from "@/components/primitives";
import { Modal, Field, inputCls, PrimaryButton, GhostButton } from "@/components/Modal";
import { StatusBadge } from "@/components/StatusBadge";
import { AudienceSelector } from "@/components/AudienceSelector";
import { toast } from "sonner";
import { MagnifyingGlass, Folder, LockKey, Plus, ArrowSquareOut } from "@phosphor-icons/react";

export default function Documents() {
  const { currentId, current } = useProjects();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showRestricted, setShowRestricted] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", category: "", restricted: false, source_url: "" });

  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/projects/${currentId}/documents`, { params: { q: q || undefined, include_restricted: showRestricted } });
      setItems(data);
    } finally { setLoading(false); }
  }, [currentId, q, showRestricted]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const openEdit = (it) => { setForm({ source_url: it.source_url || "", category: it.category || "", restricted: it.restricted, audiences: it.audiences || ["internal"] }); setEditing(it); };
  const save = async () => {
    try { await api.put(`/api/documents/${editing.id}`, { source_url: form.source_url || null, category: form.category, restricted: form.restricted, audiences: form.audiences }); toast.success("Updated"); setEditing(null); load(); }
    catch { toast.error("Update failed"); }
  };
  const create = async () => {
    if (!newItem.name.trim()) { toast.error("Name required"); return; }
    try { await api.post(`/api/projects/${currentId}/documents`, { ...newItem, source_url: newItem.source_url || null }); toast.success("Folder added"); setCreating(false); setNewItem({ name: "", category: "", restricted: false, source_url: "" }); load(); }
    catch { toast.error("Create failed"); }
  };

  if (!currentId) return <div className="cm-label animate-pulse">Loading…</div>;

  return (
    <div data-testid="documents-page">
      <PageHeader
        eyebrow="Documents"
        title="Folder Index"
        description="Initial folder index — not a complete ingestion. Source links preserve Drive's existing access controls. Sensitive accounting, payroll and PII folders are flagged Restricted and kept out of the general gallery."
      >
        {isWriter(user) && <PrimaryButton data-testid="documents-add-btn" onClick={() => setCreating(true)}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add folder</span></PrimaryButton>}
      </PageHeader>

      {current?.drive_root_url && (
        <a href={current.drive_root_url} target="_blank" rel="noopener noreferrer"
           className="mb-6 inline-flex items-center gap-2 border border-[#2C2C2A] bg-[#1A1A18] px-4 py-2.5 text-sm text-[#F59E0B] hover:bg-[#242422]" data-testid="documents-root-link">
          <ArrowSquareOut size={16} /> Open source Drive root folder
        </a>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A19D]" />
          <input data-testid="documents-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search folders…"
            className="w-full rounded-sm border border-[#2C2C2A] bg-[#1A1A18] py-2 pl-9 pr-3 text-sm text-[#F9F9F8] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#A1A19D]">
          <input type="checkbox" data-testid="documents-restricted-toggle" checked={showRestricted} onChange={(e) => setShowRestricted(e.target.checked)} />
          Show restricted folders
        </label>
      </div>

      {loading ? <div className="cm-label animate-pulse">Loading…</div> : items.length === 0 ? (
        <EmptyState title="No folders match" description="Try a different search or show restricted folders." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((d) => (
            <div key={d.id} className={`border bg-[#1A1A18] p-5 ${d.restricted ? "border-[rgba(245,158,11,0.3)]" : "border-[#2C2C2A]"}`} data-testid={`document-card-${d.order}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {d.restricted ? <LockKey size={20} className="text-[#F59E0B]" /> : <Folder size={20} className="text-[#A1A19D]" />}
                  <span className="text-[#F9F9F8]">{d.name}</span>
                </div>
                {d.restricted && <StatusBadge status="Restricted" data-testid={`document-restricted-${d.order}`} />}
              </div>
              <div className="cm-label mt-3">{d.category || "—"}</div>
              <div className="mt-3 flex items-center justify-between">
                <SourceLink url={d.source_url} label="Open folder" testid={`document-link-${d.order}`} />
                {isWriter(user) && <button onClick={() => openEdit(d)} className="text-xs text-[#F59E0B] hover:underline" data-testid={`document-edit-${d.order}`}>Edit</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Edit · ${editing?.name}`} testid="document-edit-modal">
        <Field label="Source URL (Drive link)"><input className={inputCls} data-testid="document-url" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://drive.google.com/…" /></Field>
        <Field label="Category"><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
        <Field label="Restricted">
          <label className="flex items-center gap-2 text-sm text-[#F9F9F8]"><input type="checkbox" checked={form.restricted} onChange={(e) => setForm({ ...form, restricted: e.target.checked })} /> Flag as restricted (excluded from general gallery)</label>
        </Field>
        <div className="mb-4"><AudienceSelector value={form.audiences} onChange={(a) => setForm({ ...form, audiences: a })} /></div>
        <div className="flex justify-end gap-2"><GhostButton onClick={() => setEditing(null)}>Cancel</GhostButton><PrimaryButton data-testid="document-save" onClick={save}>Save</PrimaryButton></div>
      </Modal>

      <Modal open={creating} onClose={() => setCreating(false)} title="Add folder" testid="document-create-modal">
        <Field label="Name"><input className={inputCls} data-testid="document-new-name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} /></Field>
        <Field label="Category"><input className={inputCls} value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} /></Field>
        <Field label="Source URL"><input className={inputCls} value={newItem.source_url} onChange={(e) => setNewItem({ ...newItem, source_url: e.target.value })} placeholder="https://drive.google.com/…" /></Field>
        <Field label="Restricted"><label className="flex items-center gap-2 text-sm text-[#F9F9F8]"><input type="checkbox" checked={newItem.restricted} onChange={(e) => setNewItem({ ...newItem, restricted: e.target.checked })} /> Flag as restricted</label></Field>
        <div className="flex justify-end gap-2"><GhostButton onClick={() => setCreating(false)}>Cancel</GhostButton><PrimaryButton data-testid="document-create-save" onClick={create}>Add</PrimaryButton></div>
      </Modal>
    </div>
  );
}
